import * as THREE from "three";
import { itemDef, type ArmorSlot } from "./items";
import type { SlotCell } from "./inventory";

function box(
  w: number,
  h: number,
  d: number,
  color: number,
  x: number,
  y: number,
  z: number,
): THREE.Mesh {
  const mat = new THREE.MeshLambertMaterial({ color });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y, z);
  mesh.castShadow = false;
  return mesh;
}

export class CharacterView {
  readonly group = new THREE.Group();
  private head: THREE.Mesh;
  private torso: THREE.Mesh;
  private armL: THREE.Mesh;
  private armR: THREE.Mesh;
  private legL: THREE.Mesh;
  private legR: THREE.Mesh;
  private overlays = new Map<ArmorSlot, THREE.Mesh>();
  private phase = 0;
  private held: THREE.Mesh;
  bob = 0;

  constructor() {
    this.head = box(0.48, 0.48, 0.48, 0xe8b892, 0, 1.52, 0);
    const hair = box(0.5, 0.16, 0.5, 0x3d2918, 0, 0.2, 0);
    this.head.add(hair);
    const brow = box(0.36, 0.04, 0.04, 0x2a1c10, 0, 0.06, 0.24);
    this.head.add(brow);
    const eyeL = box(0.07, 0.07, 0.04, 0x1c1a16, -0.1, 0.02, 0.24);
    const eyeR = box(0.07, 0.07, 0.04, 0x1c1a16, 0.1, 0.02, 0.24);
    this.head.add(eyeL, eyeR);
    const smile = box(0.12, 0.03, 0.03, 0xb45a4a, 0, -0.1, 0.24);
    this.head.add(smile);

    this.torso = box(0.42, 0.58, 0.24, 0x3a6ea5, 0, 1.02, 0);
    this.armL = box(0.14, 0.52, 0.14, 0xe8b892, -0.29, 1.02, 0);
    this.armR = box(0.14, 0.52, 0.14, 0xe8b892, 0.29, 1.02, 0);
    this.legL = box(0.16, 0.56, 0.16, 0x3d3a36, -0.1, 0.3, 0);
    this.legR = box(0.16, 0.56, 0.16, 0x3d3a36, 0.1, 0.3, 0);

    this.held = box(0.12, 0.12, 0.36, 0xc4a46c, 0, -0.18, 0.2);
    this.armR.add(this.held);

    this.group.add(this.head, this.torso, this.armL, this.armR, this.legL, this.legR);
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.frustumCulled = false;
    });
  }

  update(
    dt: number,
    moving: boolean,
    grounded: boolean,
    flying: boolean,
    swimming: boolean,
    yaw: number,
    pitch: number,
    armor: SlotCell[],
    heldColor: number | null,
  ): void {
    this.group.rotation.y = yaw + Math.PI;
    this.head.rotation.x = THREE.MathUtils.clamp(-pitch * 0.35, -0.45, 0.45);

    const speed = flying ? 10 : swimming ? 7 : 8;
    if (moving) this.phase += dt * speed;
    else this.phase += dt * 1.4;

    const swing = moving ? Math.sin(this.phase) : Math.sin(this.phase * 0.4) * 0.08;
    const amp = flying ? 0.25 : swimming ? 0.7 : 0.7;
    this.armL.rotation.x = swing * amp;
    this.armR.rotation.x = -swing * amp;
    this.legL.rotation.x = grounded && moving ? -swing * 0.8 : swimming ? swing * 0.4 : 0;
    this.legR.rotation.x = grounded && moving ? swing * 0.8 : swimming ? -swing * 0.4 : 0;

    if (flying) {
      this.armL.rotation.z = 0.7;
      this.armR.rotation.z = -0.7;
    } else {
      this.armL.rotation.z = 0.08;
      this.armR.rotation.z = -0.08;
    }

    this.bob = moving && grounded ? Math.abs(Math.sin(this.phase * 2)) * 0.04 : 0;

    this.held.visible = heldColor !== null;
    if (heldColor !== null) {
      (this.held.material as THREE.MeshLambertMaterial).color.setHex(heldColor);
    }

    this.syncArmor(armor);
  }

  private syncArmor(armor: SlotCell[]): void {
    const slots: ArmorSlot[] = ["head", "chest", "legs", "feet"];
    const parents = [this.head, this.torso, this.legL, this.legL];
    const sizes: [number, number, number][] = [
      [0.54, 0.22, 0.54],
      [0.48, 0.62, 0.3],
      [0.2, 0.4, 0.2],
      [0.2, 0.16, 0.22],
    ];
    const pos: [number, number, number][] = [
      [0, 0.18, 0],
      [0, 0, 0],
      [0, -0.08, 0],
      [0, -0.24, 0],
    ];

    for (let i = 0; i < 4; i++) {
      const slot = slots[i]!;
      const piece = armor[i];
      let mesh = this.overlays.get(slot);
      if (!piece) {
        if (mesh) mesh.visible = false;
        continue;
      }
      const def = itemDef(piece.id);
      const c = def.color;
      const hex = (c[0] << 16) | (c[1] << 8) | c[2];
      if (!mesh) {
        const s = sizes[i]!;
        mesh = box(s[0], s[1], s[2], hex, ...pos[i]!);
        (mesh.material as THREE.MeshLambertMaterial).transparent = true;
        (mesh.material as THREE.MeshLambertMaterial).opacity = 0.88;
        parents[i]!.add(mesh);
        if (slot === "legs") {
          const other = box(s[0], s[1], s[2], hex, ...pos[i]!);
          (other.material as THREE.MeshLambertMaterial).transparent = true;
          (other.material as THREE.MeshLambertMaterial).opacity = 0.88;
          this.legR.add(other);
          mesh.userData.pair = other;
        }
        if (slot === "feet") {
          const other = box(s[0], s[1], s[2], hex, ...pos[i]!);
          this.legR.add(other);
          mesh.userData.pair = other;
        }
        this.overlays.set(slot, mesh);
      }
      mesh.visible = true;
      (mesh.material as THREE.MeshLambertMaterial).color.setHex(hex);
      const pair = mesh.userData.pair as THREE.Mesh | undefined;
      if (pair) {
        pair.visible = true;
        (pair.material as THREE.MeshLambertMaterial).color.setHex(hex);
      }
    }
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }
}
